import {
  Banner,
  Button,
  ButtonList,
  Column,
  DropdownButton,
  DropdownButtonContext,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { DashboardQueuesType } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useServerStorage } from 'utils/useServerStorage';

import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import { dashboardActions } from '../actions/dashboard';
import { DashboardQueueApiQueryParams } from '../api/types';
import { DashboardLocationQueryParams } from '../reducers/dashboard';

import { QueuesOverview } from './QueuesOverview/QueuesOverview';

interface QueuesTypeDropdownItem {
  type: DashboardQueuesType;
  label: string;
}

// An array of valid API params to check against due to lack of ability to
// compare against keys of an interface, without introducing TS transformers.
const VALID_API_QUERY_PARAMS: ReadonlyArray<
  keyof DashboardQueueApiQueryParams
> = ['limit', 'offset', 'search', 'sort_by'];

export const Dashboard = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const dashboardState = useAppSelector((state) => state.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const storage = useServerStorage('user');

  const favoriteQueueIds = useMemo(
    () => storage.data.dashboard?.favoriteQueueIds ?? [],
    [storage.data?.dashboard],
  );

  const getQueues = () => {
    const currentQueuesType = getCurrentQueuesType();
    const apiQueryParams = getValidApiQueryParams();

    apiQueryParams.is_archived =
      currentQueuesType === DashboardQueuesType.Archived;

    dispatch(
      dashboardActions.dashboardChangeQueuesType({ type: currentQueuesType }),
    );

    if (
      [DashboardQueuesType.All, DashboardQueuesType.Archived].includes(
        currentQueuesType,
      )
    ) {
      dispatch(dashboardActions.dashboardGetAllQueues(apiQueryParams));
    } else if (currentQueuesType === DashboardQueuesType.Agent) {
      dispatch(dashboardActions.dashboardGetAgentQueues(apiQueryParams));
    } else if (
      currentQueuesType === DashboardQueuesType.Favourite &&
      !!favoriteQueueIds.length
    ) {
      dispatch(
        dashboardActions.dashboardGetQueuesById({
          queue_ids: favoriteQueueIds.filter(Boolean).join(','),
          sort_by: dashboardState.meta.currentPageParams.sort_by,
        }),
      );
    }
  };

  useEffect(() => {
    getQueues();
  }, [searchParams]);

  useEffectOnce(() => {
    ampli.dashboardViewed({ version: 'old' });

    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'queue_dashboard',
        },
      }),
    );

    return () => {
      dispatch(
        websocketActions.request({
          type: 'connection:unsubscribe',
          params: {
            type: 'queue_dashboard',
          },
        }),
      );
    };
  });

  const queuesTypeDropdownItems: ReadonlyArray<QueuesTypeDropdownItem> =
    useMemo(
      () => [
        {
          type: DashboardQueuesType.All,
          label: 'All queues',
        },
        {
          type: DashboardQueuesType.Agent,
          label: 'My queues',
        },
        {
          type: DashboardQueuesType.Favourite,
          label: 'Favorite queues',
        },
        {
          type: DashboardQueuesType.Archived,
          label: 'Archived queues',
        },
      ],
      [],
    );

  const getCurrentQueuesType = (): DashboardQueuesType => {
    const {
      meta: {
        currentPageParams: { type: stateType },
      },
    } = dashboardState;

    const query = queryString.parse(location.search);

    const currentQueuesType =
      queuesTypeDropdownItems.find(({ type }) => type === query.type)?.type ||
      stateType;

    return currentQueuesType;
  };

  /** Gets the valid API endpoint query params from current location query. */
  const getValidApiQueryParams = (): DashboardQueueApiQueryParams => {
    const {
      meta: { currentPageParams },
    } = dashboardState;

    const query = queryString.parse(location.search);

    return Object.keys(query)
      .filter((key) =>
        VALID_API_QUERY_PARAMS.includes(
          key as keyof DashboardQueueApiQueryParams,
        ),
      )
      .reduce((params, key) => ({ ...params, [key]: query[key] }), {
        ...currentPageParams,
      });
  };

  const updateCurrentQueuesType = (type: DashboardQueuesType): void => {
    dispatch(dashboardActions.dashboardChangeQueuesType({ type }));

    const newLocationParams: DashboardLocationQueryParams = {
      type,
      sort_by: dashboardState.meta.currentPageParams.sort_by,
    };

    const newSearchParams = new URLSearchParams(
      newLocationParams as unknown as Record<string, string>,
    );

    storage.set({
      dashboard: (draft) => {
        draft.defaultQueueType = type;
      },
    });

    setSearchParams(newSearchParams);
  };

  const currentQueuesType = getCurrentQueuesType();
  const currentQueuesTypeItem = queuesTypeDropdownItems.find(
    (item) => item.type === currentQueuesType,
  );

  const queuesTypeDropdown = (
    <DropdownButton
      buttonProps={{
        variant: 'tertiary',
      }}
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ toggleDropdown }) => (
            <ButtonList>
              {queuesTypeDropdownItems.map(({ type, label }) => {
                const isCurrentQueuesType = currentQueuesType === type;

                return (
                  <ButtonList.Item
                    key={type}
                    isSelected={isCurrentQueuesType}
                    onClick={() => {
                      if (isCurrentQueuesType) return;
                      toggleDropdown();
                      updateCurrentQueuesType(type as DashboardQueuesType);
                      ampli.dashboardQueuesTypeChanged({
                        type,
                        location: 'dropdown',
                      });
                    }}
                  >
                    {label}
                  </ButtonList.Item>
                );
              })}
            </ButtonList>
          )}
        </DropdownButtonContext.Consumer>
      }
    >
      {currentQueuesTypeItem?.label}
    </DropdownButton>
  );

  return (
    <Page>
      <Page.Head title="Queue Dashboard" controls={queuesTypeDropdown} />

      <Page.Body>
        <Column.Row noMinHeight>
          <Banner
            variant="announcement"
            headline="Level-up your customer experience"
            description={
              <Text.Paragraph as="span" variant="4">
                Explore our new Dashboard to unlock a range of exciting features
                including enhanced real-time metrics, insightful SLA analysis,
                timezone sensitivity, and customization options.
              </Text.Paragraph>
            }
            actionSlot={
              <Button
                variant="secondary"
                onClick={() => {
                  storage.set({
                    dashboard: (draft) => {
                      draft.prefersNewDashboard = true;
                    },
                  });

                  ampli.dashboardSwitchToNewClicked();
                }}
              >
                Explore the new Dashboard
              </Button>
            }
          />
        </Column.Row>

        <QueuesOverview onQueuesTypeChange={updateCurrentQueuesType} />
      </Page.Body>
    </Page>
  );
};
